import { isPlatformServer } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { SettingDto, SettingService } from '@hello-unicorn/api-client';
import {
  PersistenceManager,
  StorageKeys,
} from '../app/services/persistence-manager.service';

/** Cache TTL: 1 hour */
const SETTINGS_CACHE_TTL = 60 * 60 * 1000;

/** Cached settings data structure */
interface CachedSettingsData {
  settings: SettingDto[];
  companyInfo: CompanyInfo;
}

/**
 * 회사 정보 인터페이스
 */
export interface CompanyInfo {
  /** 회사명 */
  name: string;
  /** 주소 */
  address: string;
  /** 전화번호 */
  tel: string;
  /** 이메일 */
  email: string;
  /** 사업자 등록번호 */
  registrationNumber: string;
  /** 대표자명 */
  ceo: string;
  /** 로고 URL */
  logo: string;
}

/**
 * 설정값 스토어
 * @description 앱 전역에서 사용되는 설정값을 관리하는 스토어
 * @author 정정용 <jeongyong@lepisode.team>
 */
@Injectable({ providedIn: 'root' })
export class SettingStore {
  private readonly settingService = inject(SettingService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly persistence = inject(PersistenceManager);

  /** 사이드바 상태 */
  readonly isSidebarOpen = signal<boolean>(true);

  /** 사이드바 토글 상태 */
  readonly toggleSidebar = () => {
    this.isSidebarOpen.set(!this.isSidebarOpen());
  };

  /** 설정값 로딩 상태 */
  readonly isLoading = signal<boolean>(false);

  /** 설정값 로딩 완료 여부 */
  readonly isLoaded = signal<boolean>(false);

  /** 원본 설정값 목록 */
  readonly settings = signal<SettingDto[]>([]);

  /** 회사 정보 */
  readonly companyInfo = signal<CompanyInfo>({
    name: '',
    address: '',
    tel: '',
    email: '',
    registrationNumber: '',
    ceo: '',
    logo: '',
  });

  constructor() {
    this.loadFromCacheAndFetch();
  }

  /**
   * 캐시에서 로드 후 API 호출
   * - 캐시가 있으면 즉시 사용 (빠른 UI 렌더링)
   * - 백그라운드에서 API 호출하여 최신 데이터로 갱신
   */
  private loadFromCacheAndFetch(): void {
    if (isPlatformServer(this.platformId)) return;

    // Try to load from cache first
    const cached = this.persistence.load<CachedSettingsData>(
      StorageKeys.SETTINGS_CACHE,
      { storage: 'local' }
    );

    if (cached) {
      this.settings.set(cached.settings);
      this.companyInfo.set(cached.companyInfo);
      this.isLoaded.set(true);
    }

    // Fetch from API in background
    this.fetch();
  }

  /**
   * 설정값 조회
   */
  fetch(): void {
    if (isPlatformServer(this.platformId)) return;
    if (this.isLoading()) return;

    this.isLoading.set(true);

    this.settingService.settingControllerGetSettings().subscribe({
      next: settings => {
        this.settings.set(settings);
        this.parseCompanyInfo(settings);
        this.isLoaded.set(true);

        // Save to cache
        this.saveToCache();
      },
      complete: () => {
        this.isLoading.set(false);
      },
    });
  }

  /**
   * 캐시에 저장
   */
  private saveToCache(): void {
    const data: CachedSettingsData = {
      settings: this.settings(),
      companyInfo: this.companyInfo(),
    };

    this.persistence.save(StorageKeys.SETTINGS_CACHE, data, {
      ttl: SETTINGS_CACHE_TTL,
      storage: 'local',
    });
  }

  /**
   * 특정 키의 설정값 조회
   * @param key 설정 키
   * @returns 설정값 또는 빈 문자열
   */
  getValue(key: string): string {
    return this.settings().find(s => s.key === key)?.value || '';
  }

  /**
   * 회사 정보 파싱
   * @param settings 설정값 목록
   */
  private parseCompanyInfo(settings: SettingDto[]): void {
    const info: CompanyInfo = {
      name: settings.find(s => s.key === 'COMPANY:NAME')?.value || '',
      address: settings.find(s => s.key === 'COMPANY:ADDRESS')?.value || '',
      tel: settings.find(s => s.key === 'COMPANY:TEL')?.value || '',
      email: settings.find(s => s.key === 'COMPANY:EMAIL')?.value || '',
      registrationNumber:
        settings.find(s => s.key === 'COMPANY:REGISTRATION_NUMBER')?.value ||
        '',
      ceo: settings.find(s => s.key === 'COMPANY:CEO')?.value || '',
      logo: settings.find(s => s.key === 'COMPANY:LOGO')?.value || '',
    };
    this.companyInfo.set(info);
  }
}
